import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Box, Button, Container, Grid, Link as MuiLink, Typography } from "@mui/material";
import React, { useContext } from "react";
import { Link } from "react-router-dom";
import HowItWorksSection from "../components/HowItWorksSection";
import { useAppContext } from "../context/AppContext";
import { Reveal } from "../theme/animation";
import HeroImageCarousel from "../theme/HeroImageCarousel";
import HeroSection from "../theme/HeroSection";
import Logo from "../theme/Logo";
import { ThemeContext } from "../theme/ThemeContext";
import HomePageBody from "./HomePageBody";

const HomeWhiteLabel: React.FC = () => {
    const { organization } = useAppContext();
    const { brandingSettings } = useContext(ThemeContext);
    const orgDisplayName =
        brandingSettings?.organizationName ||
        organization?.name ||
        organization?.short_name ||
        "Your Organization";

    return (
        <Box>
            <HeroSection image={<HeroImageCarousel />} minHeight="80vh" showWave={true} animateHero>
                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Box sx={{ maxWidth: 640 }}>
                                <Reveal variant="fadeUp" trigger="mount">
                                    <Typography
                                        variant="h3"
                                        component="h1"
                                        sx={{
                                            color: "white",
                                            fontWeight: "bold",
                                            mb: 2,
                                            fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" }
                                        }}
                                    >
                                        {orgDisplayName}
                                    </Typography>
                                </Reveal>
                                <Reveal variant="fadeUp" trigger="mount">
                                    <Button
                                        component={Link}
                                        to="/request-access"
                                        variant="contained"
                                        size="large"
                                        data-attr="home-white-label_access-organization"
                                        endIcon={<ChevronRightIcon />}
                                        sx={{
                                            px: 3,
                                            py: 1.5,
                                            mt: 1,
                                            mb: 3,
                                            fontWeight: "bold",
                                            fontSize: "1rem"
                                        }}
                                    >
                                        Access your Organization
                                    </Button>
                                </Reveal>
                                <Reveal variant="fadeUp" trigger="mount">
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.5,
                                            pt: 2,
                                            borderTop: "1px solid rgba(255,255,255,0.25)",
                                        }}
                                    >
                                        <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                                            <Logo height={36} variant="dark" useCustom={false} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "rgba(255,255,255,0.9)", mb: 0.75, lineHeight: 1.5 }}
                                            >
                                                Powered by Strong Mind Strong Performance — a human development platform
                                                designed to support growth through reflection, relationships, and
                                                practical skill-building. Our evidence-based system helps transform
                                                anxiety into confidence and turns everyday observations into
                                                data-informed, human-centered pathways for growth.
                                            </Typography>
                                            <MuiLink
                                                component={Link}
                                                to="/who-we-are"
                                                data-attr="home-white-label_who-we-are"
                                                underline="hover"
                                                sx={{
                                                    color: "white",
                                                    fontWeight: 600,
                                                    fontSize: "0.875rem",
                                                }}
                                            >
                                                Who we are
                                            </MuiLink>
                                        </Box>
                                    </Box>
                                </Reveal>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Reveal variant="scaleIn" trigger="mount" delay={0.2}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: { xs: "center", md: "flex-end" },
                                        alignItems: "center",
                                    }}
                                >
                                    <Logo height={280} variant="dark" />
                                </Box>
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </HeroSection>
            <HowItWorksSection />
            <HomePageBody />
        </Box>
    );
};

export default HomeWhiteLabel;
